import { ChangeEvent } from "react";
import Box from "@mui/material/Box";
import InputAdornment from "@mui/material/InputAdornment";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import { alpha } from "@mui/material/styles";
import LinkIcon from "@mui/icons-material/Link";
import { AMOUNT_ERROR_TEXT, PREFILL_TEXT } from "./constants";
import { AmountCell, formatAmount, isCellEdited } from "./dailyExpenseForm";

const EDITED_SURFACE_OPACITY = 0.08;
const EDITED_DOT_SIZE = 9;
const PREFILL_ICON_SIZE = 16;
const MANUAL_PLACEHOLDER = "กรอก";

interface SalesAmountCellProps {
  routeUuid: string;
  categoryCode: string;
  /** Names the cell for a screen reader: the route and the category. */
  label: string;
  cell: AmountCell;
  hasError: boolean;
  readOnly: boolean;
  /** Absent on a read-only table. */
  onChange?: (routeUuid: string, categoryCode: string, text: string) => void;
  onBlur?: (routeUuid: string, categoryCode: string) => void;
}

/**
 * One ฝ่ายขาย amount. A cell started from แจ้งยอดโอน carries the link mark; typed over, it turns
 * amber with a dot and its tooltip gives the original value, so head office and the branch both
 * see what was changed.
 */
export default function SalesAmountCell({
  routeUuid,
  categoryCode,
  label,
  cell,
  hasError,
  readOnly,
  onChange,
  onBlur,
}: Readonly<SalesAmountCellProps>) {
  const isPrefilled = cell.prefillAmount !== null;
  const isEdited = isCellEdited(cell);
  const handleChange = (event: ChangeEvent<HTMLInputElement>) =>
    onChange?.(routeUuid, categoryCode, event.target.value);
  const handleBlur = () => onBlur?.(routeUuid, categoryCode);

  let tooltip = "";
  if (hasError) tooltip = AMOUNT_ERROR_TEXT;
  else if (isEdited) tooltip = `แก้ทับจากแจ้งยอดโอน · ค่าเดิม ${formatAmount(cell.prefillAmount ?? 0)}`;
  else if (isPrefilled) tooltip = PREFILL_TEXT;

  return (
    <Tooltip title={tooltip}>
      <Box sx={{ position: "relative" }}>
        <TextField
          size="small"
          fullWidth
          value={cell.text}
          onChange={handleChange}
          onBlur={handleBlur}
          error={hasError}
          placeholder={isPrefilled || readOnly ? "" : MANUAL_PLACEHOLDER}
          slotProps={{
            htmlInput: {
              "aria-label": label,
              inputMode: "decimal",
              readOnly,
              style: { textAlign: "right" },
            },
            input: {
              startAdornment: isPrefilled ? (
                <InputAdornment position="start">
                  <LinkIcon color="primary" sx={{ fontSize: PREFILL_ICON_SIZE }} />
                </InputAdornment>
              ) : undefined,
            },
          }}
          sx={{
            "& .MuiInputBase-root": {
              bgcolor: (theme) => {
                if (isEdited) return alpha(theme.palette.warning.main, EDITED_SURFACE_OPACITY);
                return readOnly ? theme.palette.action.hover : theme.palette.background.paper;
              },
            },
            ...(isEdited && !hasError
              ? { "& .MuiOutlinedInput-notchedOutline": { borderColor: "warning.light" } }
              : {}),
          }}
        />
        {isEdited && (
          <Box
            sx={{
              position: "absolute",
              top: -2,
              right: -2,
              width: EDITED_DOT_SIZE,
              height: EDITED_DOT_SIZE,
              borderRadius: "50%",
              bgcolor: "warning.main",
              border: 2,
              borderColor: "background.paper",
            }}
          />
        )}
      </Box>
    </Tooltip>
  );
}
